import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api-error';
import {
  ordersClient,
  paymentHoldCode,
  type Order,
  type OrderPaymentHoldCode,
  type SwitchPaymentMethodResult,
} from '@/lib/orders-client';
import type { PaymentPreference } from '@/lib/draft-order-client';
import { describeBlocker } from '@/components/cart/cart-blockers';
import { BOARD_QUERY_ROOT } from './board/use-order-board';

/** A refusal shown inline on the order it happened to, until the operator tries again. */
export interface OrderPaymentNotice {
  orderId: string;
  code: OrderPaymentHoldCode;
}

export interface OrderPaymentActions {
  notice: OrderPaymentNotice | null;
  /**
   * For the catch block of any status change on an order (cancel, in practice): reports a hold
   * refusal with its own copy — a toast plus the inline notice — and re-reads the order. Returns
   * false, reporting nothing, for any other failure, so the caller keeps its own fallback.
   */
  reportError: (orderId: string, error: unknown) => boolean;
  /** Resolves null when the switch was refused or failed; the reason has already been reported. */
  switchPaymentMethod: (
    order: Order,
    paymentPreference: PaymentPreference,
  ) => Promise<SwitchPaymentMethodResult | null>;
}

interface UseOrderPaymentActionsOptions {
  /** The server's copy of an order these actions changed, for the surface to swap into its state. */
  onOrderChanged?: (order: Order) => void;
}

/**
 * The switch-method action and the hold-refusal mapping every order detail surface shares — the
 * board, the list, the customer profile, the transfers and coupons pages, and the inbox's start-order
 * block. Each instantiates it once and passes it to its `OrderDetailPanel`, so the codes are mapped in
 * one place rather than in five `handleStatusChange`s.
 */
export function useOrderPaymentActions({ onOrderChanged }: UseOrderPaymentActionsOptions = {}): OrderPaymentActions {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('conversations');
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<OrderPaymentNotice | null>(null);

  /** The board lanes and the orders list, which may both be showing the order. */
  const refreshOrderViews = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [BOARD_QUERY_ROOT] });
    void queryClient.invalidateQueries({ queryKey: ['orders'] });
  }, [queryClient]);

  const reportError = useCallback(
    (orderId: string, error: unknown) => {
      const code = paymentHoldCode(error);
      if (!code) return false;
      setNotice({ orderId, code });
      toast.error(t(`errors.${code}`));
      // Settled or no longer unpaid: the order moved under the operator, so show what it is now.
      refreshOrderViews();
      if (code === 'PAYMENT_ALREADY_SETTLED' || code === 'NOT_RELEASABLE') {
        ordersClient
          .getById(orderId)
          .then((response) => onOrderChanged?.(response.data))
          .catch(() => undefined);
      }
      return true;
    },
    [t, refreshOrderViews, onOrderChanged],
  );

  const switchPaymentMethod = useCallback(
    async (order: Order, paymentPreference: PaymentPreference) => {
      setNotice(null);
      try {
        const response = await ordersClient.switchPaymentMethod(order.id, { paymentPreference });
        const result = response.data;
        onOrderChanged?.(result.order);
        refreshOrderViews();
        const method = tc(`cart.payment.methods.${paymentPreference}`);
        if (result.outcome === 'confirmed') {
          toast.success(t('switchPayment.toasts.confirmed', { method }));
        } else {
          const first = result.blockers[0];
          toast.warning(
            first
              ? t('switchPayment.toasts.blocked', { reason: describeBlocker(first, `${order.currency} `, tc) })
              : t('switchPayment.toasts.blockedGeneric'),
          );
        }
        return result;
      } catch (error) {
        if (reportError(order.id, error)) return null;
        console.error('Failed to switch payment method:', error);
        toast.error(
          error instanceof ApiError && error.code === 'PAYMENT_METHOD_NOT_ACCEPTED'
            ? t('errors.PAYMENT_METHOD_NOT_ACCEPTED')
            : t('switchPayment.toasts.failed'),
        );
        return null;
      }
    },
    [t, tc, onOrderChanged, refreshOrderViews, reportError],
  );

  return useMemo(() => ({ notice, reportError, switchPaymentMethod }), [notice, reportError, switchPaymentMethod]);
}
